import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import Cookies from 'js-cookie';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProgressBar from '../components/Shared/ProgressBar';
import { format, parse, isAfter, setYear, getYear, parseISO, isValid } from 'date-fns';

const PortalWrapper = ({ children }) => (
  <div className="min-h-screen bg-slate-950 text-slate-200 selection:bg-teal-500/30 p-4 sm:p-8">
    <div className="max-w-7xl mx-auto">
      {children}
    </div>
  </div>
);

const AgencyPortal = () => {
  const { agencyId } = useParams();
  const navigate = useNavigate();

  const { plans, agencies, clients: allClients, tasks, isLoading: isContextLoading } = useAppContext();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [showWithOpenTasks, setShowWithOpenTasks] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  const authCookieName = `shift_auth_${agencyId}`;

  const agency = agencies.find(a => a.id === agencyId);
  const clients = allClients.filter(c => c.agencyId === agencyId);

  useEffect(() => {
    if (agency && Cookies.get(authCookieName) === 'true') {
      setIsAuthenticated(true);
    }
  }, [agency, authCookieName]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (agency && password === agency.password) {
      Cookies.set(authCookieName, 'true', { expires: 7 });
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Invalid agency password. Please try again.');
    }
  };

  const formatRenewalDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const slashDate = parse(dateStr, 'dd/MM/yyyy', new Date());
    if (isValid(slashDate)) return format(slashDate, 'MMM d, yyyy');
    const oldSlashDate = parse(dateStr, 'MM/dd/yyyy', new Date());
    if (isValid(oldSlashDate)) return format(oldSlashDate, 'MMM d, yyyy');
    const isoDate = parseISO(dateStr);
    if (isValid(isoDate) && dateStr.includes('-') && dateStr.split('-')[0].length === 4) {
      return format(isoDate, 'MMM d, yyyy');
    }
    try {
      const today = new Date();
      const currentYear = getYear(today);
      const parsedCycle = parse(dateStr, 'MM-dd', new Date());
      if (isValid(parsedCycle)) {
        let nextOccurrence = setYear(parsedCycle, currentYear);
        if (isAfter(today, nextOccurrence)) nextOccurrence = setYear(nextOccurrence, currentYear + 1);
        return format(nextOccurrence, 'MMM d, yyyy');
      }
    } catch (e) {}
    return dateStr;
  };

  if (isContextLoading) {
    return (
      <PortalWrapper>
        <div className="min-h-[80vh] flex items-center justify-center font-bold text-slate-500 uppercase tracking-widest animate-pulse">
          Loading Portal...
        </div>
      </PortalWrapper>
    );
  }

  if (!agency) {
    return (
      <PortalWrapper>
        <div className="p-20 text-center text-slate-500 font-bold uppercase tracking-widest">
          Agency not found.
        </div>
      </PortalWrapper>
    );
  }

  if (!isAuthenticated) {
    return (
      <PortalWrapper>
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="bg-slate-900 p-8 sm:p-10 rounded-3xl border border-slate-800 w-full max-w-md shadow-2xl">
            <div className="w-16 h-16 bg-teal-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6 text-teal-400 border border-teal-500/20 shadow-lg">
              <SafeIcon icon={FiIcons.FiLock} className="text-2xl" />
            </div>
            <h2 className="text-2xl font-black text-center text-white mb-2">{agency.name} Portal</h2>
            <p className="text-slate-500 text-center text-sm mb-8 font-medium">Please enter your agency password to continue.</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-5 py-4 text-white focus:ring-2 focus:ring-teal-500 outline-none transition-all text-center tracking-widest text-sm font-bold"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-teal-400 transition-colors"
                >
                  <SafeIcon icon={showPassword ? FiIcons.FiEyeOff : FiIcons.FiEye} className="text-lg" />
                </button>
              </div>
              {error && <p className="text-red-400 text-[10px] font-black uppercase text-center mt-3">{error}</p>}
              <button type="submit" className="w-full bg-teal-600 hover:bg-teal-500 text-white py-4 rounded-xl font-black transition-all shadow-lg shadow-teal-900/20 text-sm uppercase">
                Unlock Portal
              </button>
            </form>
          </div>
        </div>
      </PortalWrapper>
    );
  }

  const filteredClients = clients
    .filter(c => (c.name || '').toLowerCase().includes(search.toLowerCase()))
    .filter(c => selectedPlan ? c.planId === selectedPlan : true)
    .filter(c => {
      if (!showWithOpenTasks) return true;
      return tasks.filter(t => t.clientId === c.id && t.status === 'Open').length > 0;
    });

  const getFaviconUrl = (url) => {
    if (!url) return null;
    try {
      const hostname = new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
      return `https://www.google.com/s2/favicons?domain=${hostname}&sz=128`;
    } catch (e) {
      return `https://www.google.com/s2/favicons?domain=${url}&sz=128`;
    }
  };

  return (
    <PortalWrapper>
      <div className="space-y-8 sm:space-y-10 animate-in fade-in duration-700 pb-20">
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 bg-slate-900/50 p-6 sm:p-8 rounded-3xl border border-slate-800">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: agency.color }}></div>
              <p className="text-slate-500 font-bold text-xs sm:text-sm uppercase tracking-widest">Partner Portal</p>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-white uppercase">{agency.name}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/portal/${agencyId}/task-log`}
              className="px-4 py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-colors flex items-center gap-2"
            >
              <SafeIcon icon={FiIcons.FiClock} /> Task Log
            </Link>
            <button onClick={() => { Cookies.remove(authCookieName); setIsAuthenticated(false); }} className="text-slate-500 hover:text-white font-black text-xs sm:text-sm uppercase tracking-widest flex items-center gap-2 transition-colors">
              <SafeIcon icon={FiIcons.FiLogOut} /> Logout
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 font-bold">
          <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800">
            <p className="text-slate-500 text-sm uppercase mb-1">Accounts</p>
            <p className="text-3xl sm:text-4xl font-black text-white">{clients.length}</p>
          </div>
          <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800">
            <p className="text-slate-500 text-sm uppercase mb-1">Status</p>
            <p className="text-3xl sm:text-4xl font-black text-teal-400">Active</p>
          </div>
          <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 sm:col-span-2 lg:col-span-1">
            <p className="text-slate-500 text-sm uppercase mb-1">Level</p>
            <p className="text-3xl sm:text-4xl font-black text-indigo-400">Partner</p>
          </div>
        </div>

        <div>
          <div className="flex flex-col md:flex-row justify-between items-stretch md:items-end mb-8 gap-6 md:gap-4">
            <h2 className="text-2xl font-black text-white flex items-center gap-3">
              <SafeIcon icon={FiIcons.FiUsers} className="text-teal-400" /> Performance
            </h2>
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="flex bg-slate-900 p-1 rounded-2xl border border-slate-800">
                <button onClick={() => setViewMode('grid')} className={`flex-1 sm:px-4 py-2 rounded-xl transition-all font-bold text-sm ${viewMode === 'grid' ? 'bg-teal-600 text-white' : 'text-slate-500'}`}><SafeIcon icon={FiIcons.FiGrid} /></button>
                <button onClick={() => setViewMode('table')} className={`flex-1 sm:px-4 py-2 rounded-xl transition-all font-bold text-sm ${viewMode === 'table' ? 'bg-teal-600 text-white' : 'text-slate-500'}`}><SafeIcon icon={FiIcons.FiList} /></button>
              </div>

              <label className="flex items-center justify-center gap-2 text-slate-400 font-bold text-sm cursor-pointer hover:text-white transition-colors bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 select-none">
                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${showWithOpenTasks ? 'bg-teal-500 border-teal-500' : 'bg-slate-950 border-slate-700'}`}>
                  {showWithOpenTasks && <SafeIcon icon={FiIcons.FiCheck} className="text-white text-[10px]" />}
                </div>
                Open Tasks
              </label>

              <div className="relative flex-1 sm:w-48">
                <select
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:ring-1 focus:ring-teal-500 outline-none font-bold appearance-none"
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                >
                  <option value="">All Plans</option>
                  {plans.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <SafeIcon icon={FiIcons.FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              </div>
              <div className="relative flex-1 sm:w-64">
                <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input type="text" placeholder="Search..." className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-9 pr-4 py-3 text-sm text-white focus:ring-1 focus:ring-teal-500 outline-none font-bold" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
          </div>

          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredClients.map(client => {
                const plan = plans.find(p => p.id === client.planId) || {};
                const usedHours = client.usedHours;
                const openTasksCount = tasks.filter(t => t.clientId === client.id && t.status === 'Open').length;

                return (
                  <Link key={client.id} to={`/portal/${agencyId}/client/${client.id}`} className="bg-slate-900 overflow-hidden rounded-3xl border border-slate-800 hover:bg-slate-800/50 hover:border-teal-900 transition-all flex flex-col h-full group">
                    <div className="p-6 pb-2 flex items-start gap-4">
                      <div className="relative shrink-0">
                        {openTasksCount > 0 && (
                          <div className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] font-black z-10 border-2 border-slate-900 shadow-md">
                            {openTasksCount}
                          </div>
                        )}
                        <div className="w-12 h-12 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-center overflow-hidden shadow-lg">
                          {client.website ? (
                            <img
                              src={getFaviconUrl(client.website)}
                              alt={client.name}
                              className="w-8 h-8 object-contain"
                              onError={(e) => { e.target.parentElement.innerHTML = `<span class="text-lg font-black text-slate-500">${(client.name || '?')[0]}</span>`; }}
                            />
                          ) : (
                            <span className="text-lg font-black text-slate-500">{client.name ? client.name[0] : '?'}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0 pr-2">
                        <h3 className="text-lg sm:text-xl font-bold text-white mb-1 line-clamp-1 group-hover:text-teal-400 uppercase transition-colors">{client.name}</h3>
                        <div className="flex flex-wrap gap-1.5">
                          <span className="text-xs px-2 py-0.5 rounded font-black uppercase border" style={{
                            backgroundColor: plan.color ? `${plan.color}15` : '#33415515',
                            color: plan.color || '#64748b',
                            borderColor: plan.color ? `${plan.color}30` : '#47556930'
                          }}>
                            {plan.name || 'Account'}
                          </span>
                        </div>
                        {(client.domain || client.notes) && (
                          <div className="mt-3 space-y-1.5">
                            {client.domain && (
                              <p className="text-xs text-slate-400 line-clamp-1 font-medium normal-case flex items-center gap-1.5">
                                <SafeIcon icon={FiIcons.FiGlobe} className="shrink-0 text-slate-500" />
                                {client.domain}
                              </p>
                            )}
                            {client.notes && (
                              <p className="text-xs text-slate-500 line-clamp-2 font-medium normal-case flex items-start gap-1.5">
                                <SafeIcon icon={FiIcons.FiFileText} className="shrink-0 mt-0.5 text-slate-600" />
                                {client.notes}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="p-6 pt-4 mt-auto space-y-4 font-bold uppercase tracking-wide">
                      <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-slate-500">Utilization</span>
                        <span className="text-teal-400">{usedHours.toFixed(1)} / {plan.monthlyHours || 0}h</span>
                      </div>
                      <ProgressBar used={usedHours} total={plan.monthlyHours || 0} colorClass="bg-teal-500" />
                      <div className="text-[10px] sm:text-xs text-slate-600"> Renewal: {formatRenewalDate(client.resetDate)} </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden shadow-2xl">
              <div className="overflow-x-auto scrollbar-hide">
                <table className="w-full text-left min-w-[600px]">
                  <thead>
                    <tr className="border-b border-slate-800 bg-slate-800/30">
                      <th className="px-6 py-5 text-sm font-black text-slate-500 uppercase tracking-widest">Client</th>
                      <th className="px-6 py-5 text-sm font-black text-slate-500 uppercase tracking-widest">Utilization</th>
                      <th className="px-6 py-5 text-sm font-black text-slate-500 uppercase tracking-widest">Renewal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {filteredClients.map(client => {
                      const plan = plans.find(p => p.id === client.planId) || {};
                      const usedHours = client.usedHours;
                      const openTasksCount = tasks.filter(t => t.clientId === client.id && t.status === 'Open').length;

                      return (
                        <tr key={client.id} className="hover:bg-slate-800/30 transition-colors group cursor-pointer" onClick={() => navigate(`/portal/${agencyId}/client/${client.id}`)}>
                          <td className="px-6 py-5">
                            <div className="flex items-center gap-2 mb-0.5">
                              <p className="font-bold text-slate-200 group-hover:text-teal-400 transition-colors uppercase text-sm">{client.name}</p>
                              {openTasksCount > 0 && (
                                <span className="bg-red-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full">{openTasksCount}</span>
                              )}
                            </div>
                            <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">{plan.name || 'Tier'}</p>
                            {(client.domain || client.notes) && (
                              <div className="mt-2 space-y-1">
                                {client.domain && <p className="text-[10px] text-slate-400 truncate max-w-[250px] normal-case tracking-normal font-medium"><SafeIcon icon={FiIcons.FiGlobe} className="inline mr-1.5 text-slate-500" />{client.domain}</p>}
                                {client.notes && <p className="text-[10px] text-slate-500 truncate max-w-[250px] normal-case tracking-normal font-medium"><SafeIcon icon={FiIcons.FiFileText} className="inline mr-1.5 text-slate-600" />{client.notes}</p>}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-5">
                            <div className="w-40 sm:w-48">
                              <div className="flex justify-between text-xs font-black uppercase mb-1">
                                <span className="text-teal-400">{usedHours.toFixed(1)}h</span>
                                <span className="text-slate-600">/ {plan.monthlyHours || 0}h</span>
                              </div>
                              <ProgressBar used={usedHours} total={plan.monthlyHours || 0} colorClass="bg-teal-500" />
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <span className="text-xs sm:text-sm font-bold text-slate-500 uppercase">{formatRenewalDate(client.resetDate)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </PortalWrapper>
  );
};

export default AgencyPortal;